import React, { useState, useEffect } from 'react';
import tw from 'twin.macro';
import styled from 'styled-components';
import AnimationRevealPage from 'helpers/AnimationRevealPage.js';
import { useSelector } from 'react-redux';
import { format, parse } from 'date-fns';
import { obtenerPedidosParaCadete } from 'scripts/obtenerPedidosParaCadete';
import { obtenerPedidosEntregadosSemana } from 'scripts/obtenerPedidosEntregadosSemana';
import { marcarPedidoComoEntregado } from 'scripts/marcarPedidoComoEntregado';
import { reasignarPedidoCadete } from 'scripts/reasignarPedidoCadete';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { crearNotaCadete } from 'scripts/crearNotaCadete';
import { toast } from 'react-toastify';

// Styled Components
const Container = tw.div`p-6 bg-white rounded-lg w-full mx-auto mt-8`;
const PedidoItemBase = tw.div`flex justify-between items-center border-b pb-2 mb-2 p-4 rounded-lg shadow-md bg-gray-100`;
const ButtonContainer = tw.div`flex items-center justify-start`;
const ButtonPanel = tw.button`px-4 py-2 mr-2 bg-green-500 text-white rounded hover:bg-green-700`;
const AssignedPedidoItem = tw(PedidoItemBase)`bg-[#99cdb6]`;
const PedidoAtrasado = tw(PedidoItemBase)`bg-[#ffc4c4]`;
const BluePedidoItem = tw(PedidoItemBase)`bg-blue-100`;
const GrayPedidoItem = styled(PedidoItemBase)`
	background-color: #f0f0f0;
`;
const SectionTitle = tw.h3`text-lg font-bold cursor-pointer flex justify-between items-center`;
const Divider = tw.hr`my-2 border-t border-gray-300`;
const ProductosListContainer = tw.div`mt-4 w-full`;
const ProductoItemStyled = tw.div`py-2`;
const ProductoNombre = tw.div`text-gray-700 font-medium`;
const ProductoCantidad = tw.div`text-gray-600 w-24`;
const ExpandIcon = styled.span`
	display: inline-block;
	margin-left: 8px;
	transition: transform 0.2s;
	${({ expanded }) => expanded && 'transform: rotate(180deg);'}
`;
const PedidoBadge = styled.span`
	display: inline-block;
	margin-left: 8px;
	padding: 2px 8px;
	border-radius: 9999px;
	background-color: #1d4ed8;
	color: #ffffff;
	font-size: 12px;
	font-weight: 600;
`;
const NotasPedidoClamp = styled.p`
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
	white-space: pre-wrap;
`;

const esPedidoAdmin = (pedido) =>
	Boolean(pedido?.es_pedido_admin) || String(pedido?.cliente_nombre || '').trim().toLowerCase() === 'cliente generico';

// Helper Components
const ProductosList = ({ detalles, productoValido }) => (
	<ProductosListContainer>
		{detalles
			?.filter((detalle) => productoValido(detalle))
			.map((detalle, index) => (
				<ProductoItemStyled key={index}>
					<ProductoNombre>{detalle.descripcion}</ProductoNombre>
					<ProductoCantidad>Cantidad: {detalle.cantidad}</ProductoCantidad>
				</ProductoItemStyled>
			))}
	</ProductosListContainer>
);

const PedidoInfo = ({ pedido, showFechaEntrega, showCadeteInfo, showClientPhone = true, showCadeteNotas = false }) => {
	const formatFecha = (fecha) => {
		if (!fecha) return 'Sin fecha definida';
		return format(parse(fecha, 'yyyy-MM-dd', new Date()), 'dd/MM/yyyy');
	};
	const pedidoEsAdmin = esPedidoAdmin(pedido);
	const notasPedido = (pedido.notas || '').trim();
	const notasCocinera = (pedido.notas_cocinera || '').trim();
	const mostrarNotasPedido = notasPedido.length > 0 || pedidoEsAdmin;
	const notasPedidoTexto = notasPedido || 'Sin notas';

	return (
		<>
			{pedido.direccion && (
				<div>
					<p>Direccion: {pedido.direccion}</p>
					<Divider />
				</div>
			)}
			{showCadeteInfo && (
				<>
					{pedido.cadete_asignado === null ? (
						<>
							<p>No tiene cadete asignado</p>
							<Divider />
						</>
					) : (
						<>
							<p>
								Cadete Asignado: {pedido.cadete_nombre ? pedido.cadete_nombre : pedido.cadete_asignado}
							</p>
							<Divider />
						</>
					)}
				</>
			)}
			{showClientPhone && !pedidoEsAdmin && pedido.cliente_telefono && (
				<div>
					<p>Telefono del Cliente: {pedido.cliente_telefono}</p>
					<Divider />
				</div>
			)}
			{mostrarNotasPedido && (
				<div>
					<p>Notas del Pedido:</p>
					{pedidoEsAdmin ?
						<NotasPedidoClamp>{notasPedidoTexto}</NotasPedidoClamp>
					:	<p>{notasPedidoTexto}</p>}
					<Divider />
				</div>
			)}
			{notasCocinera && (
				<div>
					<p>Notas para la cocinera:</p>
					<p>{notasCocinera}</p>
					<Divider />
				</div>
			)}
			{showCadeteNotas && pedido.notas_cadete && (
				<div>
					<p>Notas del Cadete: {pedido.notas_cadete}</p>
					<Divider />
				</div>
			)}
			{showFechaEntrega && <p>Fecha de entrega: {formatFecha(pedido.fecha_entrega)}</p>}
		</>
	);
};

const PedidoButtons = ({ pedido, buttons, onMarcarEntregado, onReasignar, onNotas }) => (
	<ButtonContainer>
		{buttons.includes('entregado') && (
			<ButtonPanel
				onClick={(e) => {
					e.stopPropagation();
					onMarcarEntregado(pedido.id);
				}}
			>
				Marcar como Entregado
			</ButtonPanel>
		)}
		{buttons.includes('notas') && (
			<ButtonPanel
				onClick={(e) => {
					e.stopPropagation();
					onNotas(pedido);
				}}
			>
				Notas
			</ButtonPanel>
		)}
		{buttons.includes('asignar') && (
			<ButtonPanel
				onClick={(e) => {
					e.stopPropagation();
					onReasignar(pedido.id);
				}}
			>
				Asignar
			</ButtonPanel>
		)}
		{buttons.includes('reasignar') && (
			<ButtonPanel
				onClick={(e) => {
					e.stopPropagation();
					onReasignar(pedido.id);
				}}
			>
				Reasignar
			</ButtonPanel>
		)}
	</ButtonContainer>
);

const PedidoCard = ({
	pedido,
	StyledComponent,
	expandedPedidoId,
	toggleExpanded,
	config,
	handlers,
	productoValido,
}) => {
	const isExpanded = expandedPedidoId === pedido.id;
	const isTrasladoPonce = Boolean(pedido.retiro_local) && Number(pedido.sucursal_id) === 2;

	return (
		<div key={pedido.id}>
			<StyledComponent
				style={{
					cursor: 'pointer',
					flexDirection: 'column',
					alignItems: 'stretch',
				}}
				onClick={() => toggleExpanded(pedido.id)}
				>
					<div
						style={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'flex-start',
						}}
					>
						<div style={{ flex: 1 }}>
							<p>
								Pedido #{pedido.id}
								{pedido.cliente_nombre && ` - ${pedido.cliente_nombre}`}
								{isTrasladoPonce && <PedidoBadge>Traslado a Ponce</PedidoBadge>}
								<ExpandIcon expanded={isExpanded}>▼</ExpandIcon>
							</p>
							<Divider />
							<PedidoInfo
								pedido={pedido}
							showFechaEntrega={config.showFechaEntrega}
							showCadeteInfo={config.showCadeteInfo}
							showClientPhone={config.showClientPhone !== undefined ? config.showClientPhone : true}
							showCadeteNotas={config.showCadeteNotas || false}
						/>
					</div>
					{config.buttons.length > 0 && (
						<div>
							<PedidoButtons
								pedido={pedido}
								buttons={config.buttons}
								onMarcarEntregado={handlers.onMarcarEntregado}
								onReasignar={handlers.onReasignar}
								onNotas={handlers.onNotas}
							/>
						</div>
					)}
				</div>
				{isExpanded && <ProductosList detalles={pedido.detalles} productoValido={productoValido} />}
			</StyledComponent>
		</div>
	);
};

const PanelCadetes = () => {
	const [pedidosHoy, setPedidosHoy] = useState([]);
	const [pedidosPosteriores, setPedidosPosteriores] = useState([]);
	const [pedidosAtrasados, setPedidosAtrasados] = useState([]);
	const [pedidosEntregadosSemana, setPedidosEntregadosSemana] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [open, setOpen] = useState(false);
	const [notas, setNotas] = useState('');
	const [pedidoSeleccionado, setPedidoSeleccionado] = useState(null);
	const [expandedPedidoId, setExpandedPedidoId] = useState(null);
	const token = sessionStorage.getItem('token');
	const userId = useSelector((state) => state.usuario.userInfo.id);
	const userRole = useSelector((state) => state.usuario.userInfo.rol_id);

	const togglePedidoExpanded = (id) => {
		setExpandedPedidoId((prev) => (prev === id ? null : id));
	};

	const fetchPedidos = async () => {
		try {
			const data = await obtenerPedidosParaCadete(userId, token);
			setPedidosHoy(data.pedidosHoy);
			setPedidosPosteriores(data.pedidosPosteriores);
			setPedidosAtrasados(data.pedidosAtrasados);

			// Obtener pedidos entregados de la semana
			const entregados = await obtenerPedidosEntregadosSemana(userId, userRole, token);
			setPedidosEntregadosSemana(entregados);
		} catch (error) {
			alert(error.message);
		} finally {
			setIsLoading(false);
		}
	};

	const handleMarcarComoEntregado = async (pedidoId) => {
		try {
			await marcarPedidoComoEntregado(pedidoId, token);
			fetchPedidos();
		} catch (error) {
			alert(error.message);
		}
	};

	const handleReasignarPedido = async (pedidoId) => {
		try {
			await reasignarPedidoCadete(pedidoId, userId, token);
			fetchPedidos();
		} catch (error) {
			alert(error.message);
		}
	};

	const handleOpen = (pedido) => {
		setPedidoSeleccionado(pedido);
		setNotas(pedido.notas_cadete);
		setOpen(true);
	};

	const handleClose = () => {
		setOpen(false);
	};

	const handleInputChange = (e) => {
		setNotas(e.target.value);
	};

	const handleSaveNotas = () => {
		crearNotaCadete(notas, pedidoSeleccionado.id, sessionStorage.getItem('token'))
			.then((data) => {
				toast.success(data.message);
			})
			.catch((e) => {
				toast.error(e.error);
			});
		handleClose();
		fetchPedidos();
	};

	const productoValido = (detalle) => {
		return (
			!detalle.descripcion.includes('Mercado Pago') &&
			!detalle.descripcion.includes('Envío') &&
			!detalle.descripcion.includes('Cupon') &&
			!detalle.descripcion.includes('Retiro')
		);
	};

	const getPedidoConfig = (pedido, seccion) => {
		const isAssigned = pedido.cadete_asignado === userId;
		const isUnassigned = pedido.cadete_asignado === null;

		// Configuración para pedidos atrasados
		if (seccion === 'atrasados') {
			if (isAssigned) {
				return {
					StyledComponent: AssignedPedidoItem,
					config: {
						showFechaEntrega: true,
						showCadeteInfo: false,
						buttons: ['entregado', 'notas'],
					},
				};
			} else if (isUnassigned) {
				return {
					StyledComponent: PedidoAtrasado,
					config: {
						showFechaEntrega: true,
						showCadeteInfo: true,
						buttons: ['asignar'],
					},
				};
			} else {
				return {
					StyledComponent: BluePedidoItem,
					config: {
						showFechaEntrega: true,
						showCadeteInfo: true,
						buttons: ['reasignar'],
					},
				};
			}
		}

		// Configuración para pedidos de hoy
		if (seccion === 'hoy') {
			if (isAssigned) {
				return {
					StyledComponent: AssignedPedidoItem,
					config: {
						showFechaEntrega: false,
						showCadeteInfo: false,
						buttons: ['entregado', 'notas'],
					},
				};
			} else if (isUnassigned) {
				return {
					StyledComponent: GrayPedidoItem,
					config: {
						showFechaEntrega: false,
						showCadeteInfo: true,
						buttons: ['asignar'],
					},
				};
			} else {
				return {
					StyledComponent: BluePedidoItem,
					config: {
						showFechaEntrega: false,
						showCadeteInfo: true,
						buttons: ['reasignar'],
					},
				};
			}
		}

		// Configuración para pedidos posteriores
		if (seccion === 'posteriores') {
			return {
				StyledComponent: PedidoItemBase,
				config: {
					showFechaEntrega: true,
					showCadeteInfo: false,
					buttons: [],
				},
			};
		}

		// Configuración para pedidos entregados
		if (seccion === 'entregados') {
			return {
				StyledComponent: AssignedPedidoItem,
				config: {
					showFechaEntrega: true,
					showCadeteInfo: userRole === 1, // Mostrar info del cadete solo si es admin (rol_id 1)
					showClientPhone: false, // No mostrar teléfono del cliente en pedidos entregados
					showCadeteNotas: true, // Mostrar notas del cadete
					buttons: [],
				},
			};
		}

		return null;
	};

	const handlers = {
		onMarcarEntregado: handleMarcarComoEntregado,
		onReasignar: handleReasignarPedido,
		onNotas: handleOpen,
	};

	useEffect(() => {
		fetchPedidos();
	}, []);

	return (
		<AnimationRevealPage>
			<Container>
				{isLoading ? (
					<p>Cargando pedidos...</p>
				) : (
					<>
						{pedidosAtrasados.length > 0 && (
							<>
								<SectionTitle>Pedidos atrasados</SectionTitle>
								{pedidosAtrasados.map((pedido) => {
									const pedidoConfig = getPedidoConfig(pedido, 'atrasados');
									return (
										<PedidoCard
											key={pedido.id}
											pedido={pedido}
											StyledComponent={pedidoConfig.StyledComponent}
											expandedPedidoId={expandedPedidoId}
											toggleExpanded={togglePedidoExpanded}
											config={pedidoConfig.config}
											handlers={handlers}
											productoValido={productoValido}
										/>
									);
								})}
								<Divider />
							</>
						)}

						<SectionTitle>Pedidos para entregar hoy</SectionTitle>
						{pedidosHoy.length > 0 ? (
							pedidosHoy.map((pedido) => {
								const pedidoConfig = getPedidoConfig(pedido, 'hoy');
								return (
									<PedidoCard
										key={pedido.id}
										pedido={pedido}
										StyledComponent={pedidoConfig.StyledComponent}
										expandedPedidoId={expandedPedidoId}
										toggleExpanded={togglePedidoExpanded}
										config={pedidoConfig.config}
										handlers={handlers}
										productoValido={productoValido}
									/>
								);
							})
						) : (
							<p>No hay pedidos para entregar hoy.</p>
						)}

						<SectionTitle>Pedidos posteriores</SectionTitle>
						{pedidosPosteriores.length > 0 ? (
							pedidosPosteriores.map((pedido) => {
								const pedidoConfig = getPedidoConfig(pedido, 'posteriores');
								return (
									<PedidoCard
										key={pedido.id}
										pedido={pedido}
										StyledComponent={pedidoConfig.StyledComponent}
										expandedPedidoId={expandedPedidoId}
										toggleExpanded={togglePedidoExpanded}
										config={pedidoConfig.config}
										handlers={handlers}
										productoValido={productoValido}
									/>
								);
							})
						) : (
							<p>No hay pedidos posteriores a hoy.</p>
						)}

						<SectionTitle>Pedidos Entregados Esta Semana</SectionTitle>
						{pedidosEntregadosSemana.length > 0 ? (
							pedidosEntregadosSemana.map((pedido) => {
								const pedidoConfig = getPedidoConfig(pedido, 'entregados');
								return (
									<PedidoCard
										key={pedido.id}
										pedido={pedido}
										StyledComponent={pedidoConfig.StyledComponent}
										expandedPedidoId={expandedPedidoId}
										toggleExpanded={togglePedidoExpanded}
										config={pedidoConfig.config}
										handlers={handlers}
										productoValido={productoValido}
									/>
								);
							})
						) : (
							<p>No hay pedidos entregados esta semana.</p>
						)}
					</>
				)}
				{/* Formulario para agregar o editar adicional */}
				<Dialog open={open} onClose={handleClose}>
					<DialogTitle>Notas</DialogTitle>
					<DialogContent>
						<TextField
							name="notas"
							variant="outlined"
							value={notas}
							multiline
							rows={2}
							onChange={handleInputChange}
							fullWidth
							sx={{ width: 250, marginBottom: 2 }}
						/>
					</DialogContent>
					<DialogActions>
						<Button onClick={handleClose} color="secondary">
							Cancelar
						</Button>
						<Button onClick={handleSaveNotas} color="primary">
							Guardar Notas
						</Button>
					</DialogActions>
				</Dialog>
			</Container>
		</AnimationRevealPage>
	);
};

export default PanelCadetes;
